import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  IconButton, 
  Fade,
  Popper,
  Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const DemoTooltip = ({
  id,
  target,
  title,
  content,
  placement = 'bottom',
  offset = 10,
  onDismiss,
  onSkipAll,
  open = true,
  delay = 0,
  maxWidth = 450,
  arrow = true,
  currentStep,
  totalSteps
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (open && target) {
      const targetElement = typeof target === 'string' 
        ? document.querySelector(target)
        : target;
      
      if (targetElement) {
        timeoutRef.current = setTimeout(() => {
          setAnchorEl(targetElement);
          setIsVisible(true);
        }, delay);
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [open, target, delay, id]);

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => {
      onDismiss?.(id);
    }, 200);
  };

  const handleSkipAll = () => {
    setIsVisible(false);
    setTimeout(() => {
      onSkipAll?.();
    }, 200);
  };

  // Get arrow emoji based on placement
  const getArrowEmoji = () => {
    if (!arrow) return '';
    
    if (placement.startsWith('top')) return '⬇️'; // Points down to target
    if (placement.startsWith('bottom')) return '⬆️'; // Points up to target  
    if (placement.startsWith('left')) return '➡️'; // Points right to target
    if (placement.startsWith('right')) return '⬅️'; // Points left to target
    return '⬆️'; // Default
  };

  if (!anchorEl) {
    return null;
  }

  const arrowEmoji = getArrowEmoji();

  return (
    <Popper
      open={isVisible}
      anchorEl={anchorEl}
      container={document.body}
      placement={placement}
      style={{ zIndex: 99999 }}
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 12],
          },
        },
        {
          name: 'preventOverflow',
          options: {
            boundary: 'viewport',
            padding: 8,
            altAxis: true,
            tether: false,
          },
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: ['top', 'left', 'right'],
          },
        },
        {
          name: 'computeStyles',
          options: {
            adaptive: false,
            enabled: true,
          },
        },
      ]}
    >
      <Fade in={isVisible} timeout={200}>
        <Paper
          elevation={8}
          sx={{
            width: `${maxWidth}px`,
            maxWidth: `${maxWidth}px`,
            minWidth: '400px',
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            color: 'white',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 10px 30px rgba(25, 118, 210, 0.3)',
            position: 'relative',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              pb: title ? 1 : 2,
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
            }}
          >
            {title && (
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  fontSize: '1rem',
                  color: 'white',
                  flex: 1,
                  pr: 2
                }}
              >
                {title}
              </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                onClick={handleSkipAll}
                size="small"
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '0.75rem',
                  minWidth: 'auto',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: '6px',
                  textTransform: 'none',
                  '&:hover': {
                    color: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                Skip All
              </Button>
              <IconButton
                onClick={handleDismiss}
                size="small"
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  '&:hover': {
                    color: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Content */}
          <Box sx={{ px: 2, pb: 2, pt: 1 }}>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.95)',
                lineHeight: 1.5,
                fontSize: '0.9rem'
              }}
            >
              {arrowEmoji ? `${arrowEmoji} ${content}` : content}
            </Typography>
          </Box>
        </Paper>
      </Fade>
    </Popper>
  );
};

export default DemoTooltip; 